import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

const headerCellSx = {
  border: "none",
  bgcolor: "#EEF0F1",
  color: "#454545",
  fontSize: 12,
  fontWeight: 700,
  py: 1,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  border: "none",
  borderBottom: "1px solid #E0E0E0",
  color: "#454545",
  fontSize: 13,
  py: 1.5,
};

// Maps each sortable column id to how its value should be read off a row.
const COLUMNS = [
  { id: "accountName", label: "Account Name", align: "left", width: "28%" },
  { id: "lessThan48", label: "<48 mcg", align: "right", width: "10%" },
  { id: "dose48", label: "48 mcg", align: "right", width: "10%" },
  { id: "dose64", label: "64 mcg", align: "right", width: "10%" },
  { id: "dose80", label: "80 mcg", align: "right", width: "10%" },
  { id: "greaterThan80", label: ">80 mcg", align: "right", width: "10%" },
];

function TableSkeleton() {
  return (
    <Stack spacing={1.2} sx={{ py: 1 }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Skeleton key={index} variant="rounded" height={30} />
      ))}
    </Stack>
  );
}

function getComparableValue(row, columnId) {
  const value = row[columnId];
  if (columnId === "accountName") {
    return (value || "").toString().toLowerCase();
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : -Infinity;
}

function compareRows(a, b, columnId, direction) {
  const aValue = getComparableValue(a, columnId);
  const bValue = getComparableValue(b, columnId);

  let result = 0;
  if (aValue < bValue) result = -1;
  else if (aValue > bValue) result = 1;

  return direction === "asc" ? result : -result;
}

export default function TyvasoDpiDosageDistributionCard({
  title = "Tyvaso DPI Patient Dosage Distribution",
  subtitle = "Active Patients for each dose",
  rows = [],
  isLoading = false,
  isError = false,
  onExplore,
}) {
  const [orderBy, setOrderBy] = useState("dose80");
  const [direction, setDirection] = useState("asc");

  const hasRows = Array.isArray(rows) && rows.length > 0;

  const sortedRows = useMemo(() => {
    if (!hasRows) return rows;
    return [...rows].sort((a, b) => compareRows(a, b, orderBy, direction));
  }, [rows, hasRows, orderBy, direction]);

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(columnId);
      setDirection("asc");
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontSize: 14, fontWeight: 600 }}
          >
            {title}
          </Typography>
          {onExplore && (
            <Button
              variant="text"
              size="small"
              onClick={() => onExplore()}
              sx={{
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                color: "#D96B04",
                minWidth: "auto",
                p: 0,
                flexShrink: 0,
                marginRight: "30px",
              }}
            >
              Explore
            </Button>
          )}
        </Box>
        <Typography
          sx={{ mb: 2, fontSize: 14, color: "#6B7280", flexShrink: 0 }}
        >
          {subtitle}
        </Typography>

        {isLoading ? (
          <TableSkeleton />
        ) : (
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              pb: MY_PERF_SCROLL_LIST_PB,
            }}
          >
            {!hasRows ? (
              <Box
                sx={{
                  minHeight: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#718096", fontSize: 14 }}>
                  {isError
                    ? "Unable to load dosage distribution"
                    : "No data found"}
                </Typography>
              </Box>
            ) : (
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sortDirection={
                          orderBy === column.id ? direction : false
                        }
                        sx={{ ...headerCellSx, width: column.width }}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? direction : "asc"}
                          onClick={() => handleSort(column.id)}
                          sx={{
                            "&.MuiTableSortLabel-root": { color: "#454545" },
                            "&.Mui-active": { color: "#454545" },
                            "& .MuiTableSortLabel-icon": {
                              color: "#454545 !important",
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontWeight: 600,
                          wordBreak: "break-word",
                        }}
                      >
                        {row.accountName || "—"}
                      </TableCell>
                      {[
                        row.lessThan48,
                        row.dose48,
                        row.dose64,
                        row.dose80,
                        row.greaterThan80,
                      ].map((value, index) => (
                        <TableCell
                          key={`${row.id}-dose-${index}`}
                          align="right"
                          sx={bodyCellSx}
                        >
                          {value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
